import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWaveformLines } from "@fortawesome/pro-regular-svg-icons/faWaveformLines";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faVolumeSlash } from "@fortawesome/pro-regular-svg-icons/faVolumeSlash";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useResultFile } from "./useFormResultUpload";

interface Props {
  element: DocumentElementType;
}

/**
 * Read-only render of a page-document audio block — a labelled row with an
 * inline `<audio>` player. The editor's rich version (record/upload picker)
 * lives in apps/web; this is the shared read-only renderer.
 */
const PageAudioView: React.FC<Props> = ({ element }) => {
  const data = (element.data ?? {}) as { attachmentId?: number | null };
  const attachmentId =
    typeof data.attachmentId === "number" ? data.attachmentId : null;
  const { file, missing } = useResultFile(attachmentId);

  if (attachmentId == null) return null;

  // Still looking the file up, as opposed to gone or withheld from this reader.
  const waiting = !file && !missing;

  return (
    <div className="rounded-[10px] border border-[#e7e5e1] bg-white px-[14px] py-[11px]">
      <div className="flex items-center gap-[11px]">
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] bg-[#f3f1ee] text-[#8f8b83]">
          <FontAwesomeIcon
            icon={file ? faWaveformLines : missing ? faVolumeSlash : faSpinner}
            className={waiting ? "animate-spin text-[14px]" : "text-[15px]"}
          />
        </span>
        <span
          className={
            "min-w-0 flex-1 truncate font-jakarta text-[13.5px] font-semibold " +
            (missing ? "text-[#8f8b83]" : "text-[#33302b]")
          }
        >
          {missing ? "Audio unavailable" : file?.name ?? "Audio"}
        </span>
      </div>
      {file?.url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={file.url} controls preload="none" className="mt-[10px] w-full" />
      )}
    </div>
  );
};

export default PageAudioView;
